import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Grid, Tabs, Tab, CircularProgress, Alert, Chip,
} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';

import {
  getAiEnrichmentJobsReport, getAiProvidersReport, getAiFeedbackReport,
  getAiPromptsReport, getAiFreshnessReport, getAiDomainHealthReport, getAiCoverageReport,
} from '../../services/api';
import KpiCard from '../../components/reports/KpiCard';
import DistributionBar from '../../components/reports/DistributionBar';
import TableWithBars from '../../components/reports/TableWithBars';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import ReportExportButton from '../../components/reports/ReportExportButton';

const fmtPct = (v, d = 1) => v == null ? '—' : `${(v * 100).toFixed(d)}%`;
const fmtInt = (v) => v == null ? '—' : Number(v).toLocaleString();
const fmtUsd = (v) => v == null ? '—' : `$${Number(v).toFixed(2)}`;

function ReportSection({ title, action, children }) {
  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function useReport(loader, ...args) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const argsKey = JSON.stringify(args);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await loader(...args);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.title || e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [loader, argsKey]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, error };
}

// A1
function EnrichmentJobsCard({ filters }) {
  const { data, loading, error } = useReport(getAiEnrichmentJobsReport, filters);
  const d = data?.data || {};
  const totals = d.totals || {};
  const recent = d.recent || [];
  const status = d.byStatus || {};
  return (
    <ReportSection title="A1 · Enrichment Job Throughput">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {Object.entries(status).map(([k, v]) => (
              <Grid item xs={6} md={2} key={k}>
                <KpiCard label={k} value={fmtInt(v)} tone={k === 'Failed' ? 'negative' : k === 'Completed' ? 'positive' : 'info'} />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}><KpiCard label="Items" value={fmtInt(totals.items)} /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Succeeded" value={fmtPct(totals.succeededRate)} sub={fmtInt(totals.succeeded)} tone="positive" /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Failed" value={fmtPct(totals.failedRate)} sub={fmtInt(totals.failed)} tone={totals.failedRate > 0.05 ? 'negative' : 'neutral'} /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Throughput" value={totals.throughputItemsPerMin == null ? '—' : `${totals.throughputItemsPerMin}/min`} sub={totals.avgDurationMin != null ? `Avg ${totals.avgDurationMin}m` : null} tone="info" /></Grid>
          </Grid>
          <TableWithBars
            barColumn="total"
            columns={[
              { key: 'jobId', label: 'Job', render: (r) => `#${r.jobId}` },
              { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} /> },
              { key: 'provider', label: 'Provider', render: (r) => r.provider || '—' },
              { key: 'total', label: 'Total', align: 'right' },
              { key: 'succeeded', label: 'Succ', align: 'right' },
              { key: 'failed', label: 'Fail', align: 'right' },
              { key: 'durationMin', label: 'Duration', align: 'right', render: (r) => r.durationMin != null ? `${r.durationMin}m` : '—' },
              { key: 'createdAt', label: 'Created', render: (r) => new Date(r.createdAt).toLocaleString() },
            ]}
            rows={recent.map((r) => ({ id: r.jobId, ...r }))}
          />
        </>
      )}
    </ReportSection>
  );
}

// A2
function ProvidersCard({ filters }) {
  const { data, loading, error } = useReport(getAiProvidersReport, filters);
  const providers = data?.data?.providers || [];
  const totals = data?.data?.totals || {};
  return (
    <ReportSection
      title="A2 · Provider Mix & Cost"
      action={<ReportExportButton path="/reports/ai-ops/providers/export.csv" filters={filters} filename="ai-providers.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <KpiCard label="Total calls" value={fmtInt(totals.calls)} />
            <KpiCard label="Total tokens" value={fmtInt(totals.tokens)} />
            <KpiCard label="Total cost" value={fmtUsd(totals.costUsd)} tone="info" />
            <KpiCard label="Error rate" value={fmtPct(totals.errorRate)} tone={totals.errorRate > 0.05 ? 'negative' : 'positive'} />
          </Stack>
          <TableWithBars
            barColumn="calls"
            columns={[
              { key: 'provider', label: 'Provider' },
              { key: 'calls', label: 'Calls', align: 'right' },
              { key: 'tokens', label: 'Tokens', align: 'right', render: (r) => fmtInt(r.tokens) },
              { key: 'costUsd', label: 'Cost', align: 'right', render: (r) => fmtUsd(r.costUsd) },
              { key: 'errorRate', label: 'Err %', align: 'right', render: (r) => fmtPct(r.errorRate) },
              { key: 'avgLatencyMs', label: 'Latency', align: 'right', render: (r) => r.avgLatencyMs != null ? `${r.avgLatencyMs}ms` : '—' },
            ]}
            rows={providers.map((r, i) => ({ id: r.provider + i, ...r }))}
          />
        </>
      )}
    </ReportSection>
  );
}

// A3
function FeedbackCard({ filters }) {
  const { data, loading, error } = useReport(getAiFeedbackReport, filters);
  const totals = data?.data?.totals || {};
  const byOp = data?.data?.byEntityOp || [];
  const hotspots = data?.data?.wrongDataHotspots || [];
  return (
    <ReportSection
      title="A3 · AI Feedback Ledger"
      action={<ReportExportButton path="/reports/ai-ops/feedback/export.csv" filters={filters} filename="ai-feedback.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <KpiCard label="Useful" value={fmtInt(totals.Useful)} tone="positive" />
            <KpiCard label="Strong fit" value={fmtInt(totals.StrongFit)} tone="positive" />
            <KpiCard label="Not relevant" value={fmtInt(totals.NotRelevant)} />
            <KpiCard label="Wrong data" value={fmtInt(totals.WrongData)} tone={totals.WrongData > 0 ? 'negative' : 'neutral'} />
          </Stack>
          <TableWithBars
            barColumn="useful"
            columns={[
              { key: 'entityType', label: 'Entity' },
              { key: 'aiOperation', label: 'Operation' },
              { key: 'useful', label: 'Useful', align: 'right' },
              { key: 'strongFit', label: 'Strong fit', align: 'right' },
              { key: 'notRelevant', label: 'Not relevant', align: 'right' },
              { key: 'wrong', label: 'Wrong', align: 'right', render: (r) => r.wrong > 0 ? <Chip size="small" label={r.wrong} color="error" /> : '0' },
            ]}
            rows={byOp.map((r, i) => ({ id: i, ...r }))}
          />
          {hotspots.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>WrongData hotspots</Typography>
              <DistributionBar
                rows={hotspots.map((h) => ({ label: `${h.entityType}/${h.aiOperation}`, value: h.wrong, color: '#dc2626' }))}
                valueFormatter={fmtInt}
              />
            </Box>
          )}
        </>
      )}
    </ReportSection>
  );
}

// A4
function PromptsCard({ filters }) {
  const { data, loading, error } = useReport(getAiPromptsReport, filters);
  const rows = data?.data?.prompts || [];
  return (
    <ReportSection title="A4 · Prompt Version Performance">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <TableWithBars
          barColumn="samples"
          columns={[
            { key: 'version', label: 'Prompt version' },
            { key: 'samples', label: 'Samples', align: 'right' },
            { key: 'avgConfidence', label: 'Avg confidence', align: 'right', render: (r) => r.avgConfidence == null ? '—' : r.avgConfidence.toFixed(3) },
          ]}
          rows={rows.map((r, i) => ({ id: i, ...r }))}
        />
      )}
    </ReportSection>
  );
}

// A5
function FreshnessCard() {
  const { data, loading, error } = useReport(getAiFreshnessReport, {});
  const b = data?.data?.buckets || {};
  return (
    <ReportSection title="A5 · Verdict Freshness">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <DistributionBar
          rows={[
            { label: 'Fresh (<7d)', value: b.fresh || 0, color: '#16a34a' },
            { label: 'Aging (7–30d)', value: b.aging || 0, color: '#f59e0b' },
            { label: 'Stale (>30d)', value: b.stale || 0, color: '#dc2626' },
            { label: 'No verdict', value: b.noVerdict || 0, color: '#9ca3af' },
          ]}
          valueFormatter={fmtInt}
        />
      )}
    </ReportSection>
  );
}

// A6
function DomainHealthCard() {
  const { data, loading, error } = useReport(getAiDomainHealthReport);
  const t = data?.data?.totals || {};
  const last = data?.data?.lastCheckedAt;
  const recent = data?.data?.recentJobs;
  return (
    <ReportSection title="A6 · Domain Check Health">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}><KpiCard label="Reachable" value={fmtInt(t.reachable)} tone="positive" /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Unreachable" value={fmtInt(t.unreachable)} tone={t.unreachable > 0 ? 'negative' : 'neutral'} /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Recent jobs (30d)" value={fmtInt(recent)} tone="info" /></Grid>
            <Grid item xs={6} md={3}><KpiCard label="Last sweep" value={last ? new Date(last).toLocaleDateString() : '—'} /></Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary">
            Note: Per-domain status (parked / redirect) not yet tracked at lead level. Reachable/Unreachable derived from Lead.Status.
          </Typography>
        </>
      )}
    </ReportSection>
  );
}

// A7
function CoverageCard() {
  const { data, loading, error } = useReport(getAiCoverageReport);
  const d = data?.data || {};
  return (
    <ReportSection title="A7 · AI Coverage">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <DistributionBar
          rows={[
            { label: 'AiInsights present', value: d.withAiInsights?.pct || 0, note: `${d.withAiInsights?.count || 0} of ${d.totalLeads || 0}` },
            { label: 'NextOutreachHint present', value: d.withNextOutreachHint?.pct || 0, note: `${d.withNextOutreachHint?.count || 0} of ${d.totalLeads || 0}` },
            { label: 'Decided ICP fit', value: d.withDecidedIcpFit?.pct || 0, note: `${d.withDecidedIcpFit?.count || 0} of ${d.totalLeads || 0}` },
          ]}
          valueFormatter={(v) => fmtPct(v)}
          max={1}
        />
      )}
    </ReportSection>
  );
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'jobs', label: 'Enrichment Jobs (A1)' },
  { key: 'providers', label: 'Providers (A2)' },
  { key: 'feedback', label: 'Feedback (A3)' },
  { key: 'prompts', label: 'Prompts (A4)' },
  { key: 'freshness', label: 'Freshness (A5)' },
  { key: 'domain', label: 'Domain Health (A6)' },
  { key: 'coverage', label: 'Coverage (A7)' },
];

export default function AiOpsReportPage() {
  const [filters, setFilters] = useState({ window: '30d' });
  const [tab, setTab] = useState('overview');
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <SmartToyOutlinedIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>AI & Enrichment Ops Reports</Typography>
        <Box flex={1} />
        <ReportFilterBar value={filters} onChange={setFilters} />
      </Stack>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
      </Tabs>
      {tab === 'overview' && (
        <>
          <EnrichmentJobsCard filters={filters} />
          <ProvidersCard filters={filters} />
          <CoverageCard />
        </>
      )}
      {tab === 'jobs' && <EnrichmentJobsCard filters={filters} />}
      {tab === 'providers' && <ProvidersCard filters={filters} />}
      {tab === 'feedback' && <FeedbackCard filters={filters} />}
      {tab === 'prompts' && <PromptsCard filters={filters} />}
      {tab === 'freshness' && <FreshnessCard />}
      {tab === 'domain' && <DomainHealthCard />}
      {tab === 'coverage' && <CoverageCard />}
    </Box>
  );
}
